// VerifyEmailAction.jsx — the screen someone lands on from the confirmation email.
//
// REPLACES GOOGLE'S PAGE. Firebase's own handler at firebaseapp.com/__/auth/action is
// unbranded and reads as a security warning rather than a welcome; this is the same
// operation (applyActionCode against the oobCode) wearing the app's face. Our email points
// here instead — see backend/managers/verification_email.py.
//
// Reachable while UNVERIFIED, which is the entire point: App.jsx's verify wall has to make
// an exception for this route or the confirmation link is walled off by the wall it exists
// to remove.
//
// House style: no spinners, no gradients, no blur.
import React, { useEffect, useRef, useState } from "react";
import { MailCheck, AlertTriangle } from "lucide-react";
import { applyActionCode, getAuth } from "firebase/auth";
import { useT } from "../i18n/useT";

const VerifyEmailAction = () => {
  const t = useT();
  const [state, setState] = useState("working");   // "working" | "done" | "failed"
  const ran = useRef(false);

  useEffect(() => {
    // StrictMode double-invokes effects in dev. An oobCode is SINGLE USE, so a second call
    // always fails on an already-spent code and would show a failure after a real success.
    if (ran.current) return;
    ran.current = true;

    const code = new URLSearchParams(window.location.search).get("oobCode");
    if (!code) { setState("failed"); return; }

    (async () => {
      const auth = getAuth();
      try {
        await applyActionCode(auth, code);
      } catch {
        // A spent code is the common "failure" here — someone clicking the link twice, or
        // returning to the tab. If this session is already confirmed, that is a success from
        // where the person is standing, so check before calling it an error.
        try { await auth.currentUser?.reload(); } catch { /* offline — fall through */ }
        if (!auth.currentUser?.emailVerified) { setState("failed"); return; }
      }
      // The signed-in session still holds a token minted before confirmation, so reload to
      // pick up emailVerified. Without this, landing back on the app re-shows the wall.
      try { await auth.currentUser?.reload(); } catch { /* the hard nav below re-reads it */ }
      setState("done");
    })();
  }, []);

  // A HARD load, not a client nav: the verify wall is decided in App.jsx off the auth state
  // this page just changed, and a full load guarantees every downstream query refetches
  // against a confirmed session rather than a half-populated one.
  const enter = () => window.location.assign("/");

  // Send again lives on the wall, not here — this page has no session context to resend for
  // when the code is bad. Going back to the app puts them in front of that button.
  const failed = state === "failed";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-10"
         style={{ background: "#0a0a0f" }}>
      <div className="flex items-center gap-2 mb-8">
        <span className="text-[40px] font-extrabold text-[#635BFF] tracking-tight">Magi</span>
        <img src="/magi-icon.svg" alt="" className="w-12 h-12" />
      </div>

      <div className="rounded-2xl shadow-2xl max-w-sm w-full p-7 text-center"
           style={{ background: "#131313", border: "1px solid #232326" }}>
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
          style={{ background: failed ? "#3A1A1A" : "#123322" }}
        >
          {failed
            ? <AlertTriangle className="w-7 h-7 text-red-300" />
            : <MailCheck className="w-7 h-7 text-green-400" />}
        </div>

        <h1 className="text-xl font-bold text-white mb-2">
          {state === "working" && t("verify_action_working_title")}
          {state === "done" && t("verify_action_done_title")}
          {failed && t("verify_action_failed_title")}
        </h1>

        <p className="text-sm text-gray-400 leading-relaxed mb-6">
          {state === "working" && t("verify_action_working_desc")}
          {state === "done" && t("verify_action_done_desc")}
          {failed && t("verify_action_failed_desc")}
        </p>

        {state !== "working" && (
          <button
            onClick={enter}
            className="w-full py-3 px-4 bg-white text-gray-900 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
          >
            {failed ? t("verify_action_failed_cta") : t("verify_action_done_cta")}
          </button>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailAction;
